import React, { useEffect } from "react";
import { logAdminActivity } from "../utils/adminSecurity";

const SecurityProvider = ({ children }) => {
  useEffect(() => {
    // Set security headers (client-side enforcement)
    const setSecurityHeaders = () => {
      // Content Security Policy
      const metaCSP = document.createElement("meta");
      metaCSP.httpEquiv = "Content-Security-Policy";

      // Different CSP for development vs production
      const isDevelopment = process.env.NODE_ENV === "development";
      const connectSrc = isDevelopment
        ? "'self' http://localhost:* ws://localhost:* https://api.calendly.com https://firestore.googleapis.com https://identitytoolkit.googleapis.com https://securetoken.googleapis.com https://www.googleapis.com https://firebase.googleapis.com https://firebaseinstallations.googleapis.com https://firebasestorage.googleapis.com"
        : "'self' https://api.calendly.com https://firestore.googleapis.com https://identitytoolkit.googleapis.com https://securetoken.googleapis.com https://www.googleapis.com https://firebase.googleapis.com https://firebaseinstallations.googleapis.com https://firebasestorage.googleapis.com";

      // More flexible CSP for common services while maintaining security
      const scriptSrc = isDevelopment
        ? "'self' 'unsafe-inline' 'unsafe-eval' https://widget.calendly.com https://www.googletagmanager.com https://www.google-analytics.com https://ssl.google-analytics.com https://connect.facebook.net https://www.facebook.com"
        : "'self' 'unsafe-inline' 'unsafe-eval' https://widget.calendly.com https://www.googletagmanager.com https://www.google-analytics.com https://ssl.google-analytics.com https://connect.facebook.net https://www.facebook.com";

      const imgSrc =
        "'self' data: https: blob: https://www.google-analytics.com https://ssl.google-analytics.com https://www.googletagmanager.com https://www.facebook.com https://connect.facebook.net";

      // Self-hosted backend API (email, user, appointment, availability, student
      // management, documents, Google Meet). Allowed in connect-src below.
      const firebaseFunctionsUrls =
        process.env.REACT_APP_API_URL || "https://api.begtools.xyz";

      const connectSrcExtended = isDevelopment
        ? `${connectSrc} ${firebaseFunctionsUrls} https://www.google-analytics.com https://ssl.google-analytics.com https://www.googletagmanager.com https://region1.google-analytics.com https://analytics.google.com https://stats.g.doubleclick.net https://www.facebook.com https://connect.facebook.net`
        : `${connectSrc} ${firebaseFunctionsUrls} https://www.google-analytics.com https://ssl.google-analytics.com https://www.googletagmanager.com https://region1.google-analytics.com https://analytics.google.com https://stats.g.doubleclick.net https://www.facebook.com https://connect.facebook.net`;

      metaCSP.content = `default-src 'self'; script-src ${scriptSrc}; style-src 'self' 'unsafe-inline' https://fonts.googleapis.com; font-src 'self' https://fonts.gstatic.com; img-src ${imgSrc}; connect-src ${connectSrcExtended}; frame-src https://calendly.com;`;
      document.head.appendChild(metaCSP);

      // X-Frame-Options
      const metaFrameOptions = document.createElement("meta");
      metaFrameOptions.httpEquiv = "X-Frame-Options";
      metaFrameOptions.content = "DENY";
      document.head.appendChild(metaFrameOptions);

      // X-Content-Type-Options
      const metaContentType = document.createElement("meta");
      metaContentType.httpEquiv = "X-Content-Type-Options";
      metaContentType.content = "nosniff";
      document.head.appendChild(metaContentType);

      // Referrer Policy
      const metaReferrer = document.createElement("meta");
      metaReferrer.name = "referrer";
      metaReferrer.content = "strict-origin-when-cross-origin";
      document.head.appendChild(metaReferrer);
    };

    // Anti-debugging measures
    const antiDebug = () => {
      // Detect DevTools
      let devtools = {
        open: false,
        orientation: null,
      };

      const threshold = 160;

      setInterval(() => {
        if (
          window.outerHeight - window.innerHeight > threshold ||
          window.outerWidth - window.innerWidth > threshold
        ) {
          if (!devtools.open) {
            devtools.open = true;
            logAdminActivity("devtools_detected", {
              timestamp: new Date().toISOString(),
              userAgent: navigator.userAgent,
            });

            if (process.env.NODE_ENV === "production") {
              console.clear();
              console.log(
                "%cDurdur!",
                "color: red; font-size: 50px; font-weight: bold;"
              );
              console.log(
                "%cBu bir tarayıcı özelliğidir ve geliştiriciler için tasarlanmıştır. Birisi size buraya kod kopyalayıp yapıştırmanızı söylediyse, bu bir dolandırıcılık girişimidir ve onlara hesabınıza erişim izni verecektir.",
                "color: red; font-size: 16px;"
              );
            }
          }
        } else {
          devtools.open = false;
        }
      }, 500);
    };

    // Right-click protection for production (disabled for better user experience)
    const protectRightClick = (e) => {
      // Right-click is now enabled for all environments
      // if (process.env.NODE_ENV === "production") {
      //   e.preventDefault();
      //   return false;
      // }
    };

    // Key combination protection
    const protectKeyboardShortcuts = (e) => {
      if (process.env.NODE_ENV === "production") {
        // Disable F12, Ctrl+Shift+I, Ctrl+Shift+C, Ctrl+Shift+J, Ctrl+U
        if (
          e.keyCode === 123 ||
          (e.ctrlKey &&
            e.shiftKey &&
            (e.keyCode === 73 || e.keyCode === 67 || e.keyCode === 74)) ||
          (e.ctrlKey && e.keyCode === 85)
        ) {
          e.preventDefault();
          return false;
        }
      }
    };

    // Disable text selection for sensitive areas
    const disableSelection = () => {
      document.onselectstart = function () {
        return false;
      };
      document.onmousedown = function () {
        return false;
      };
    };

    // Initialize security measures
    setSecurityHeaders();

    if (process.env.NODE_ENV === "production") {
      antiDebug();
      document.addEventListener("contextmenu", protectRightClick);
      document.addEventListener("keydown", protectKeyboardShortcuts);
      // Uncomment the line below if you want to disable text selection
      // disableSelection();
    }

    // Cleanup
    return () => {
      if (process.env.NODE_ENV === "production") {
        document.removeEventListener("contextmenu", protectRightClick);
        document.removeEventListener("keydown", protectKeyboardShortcuts);
      }
    };
  }, []);

  // Monitor for suspicious activities
  useEffect(() => {
    const monitorSuspiciousActivity = () => {
      // Monitor rapid page navigation
      let navigationCount = 0;
      const resetNavigationCount = () => {
        navigationCount = 0;
      };

      const handleNavigation = () => {
        navigationCount++;
        if (navigationCount > 10) {
          logAdminActivity("suspicious_navigation", {
            count: navigationCount,
            timestamp: new Date().toISOString(),
          });
        }
      };

      // Monitor for automated tools
      const detectAutomation = () => {
        // Check for common automation indicators
        if (
          window.navigator.webdriver ||
          window.callPhantom ||
          window._phantom ||
          window.Buffer ||
          window.emit ||
          window.spawn
        ) {
          logAdminActivity("automation_detected", {
            timestamp: new Date().toISOString(),
            indicators: {
              webdriver: !!window.navigator.webdriver,
              phantom: !!(window.callPhantom || window._phantom),
              node: !!(window.Buffer || window.emit || window.spawn),
            },
          });
        }
      };

      window.addEventListener("beforeunload", handleNavigation);
      setTimeout(resetNavigationCount, 60000); // Reset every minute
      detectAutomation();

      return () => {
        window.removeEventListener("beforeunload", handleNavigation);
      };
    };

    const cleanup = monitorSuspiciousActivity();
    return cleanup;
  }, []);

  return <>{children}</>;
};

export default SecurityProvider;
